import React, { Suspense, lazy, useCallback, useEffect } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import "@fortawesome/fontawesome-free/css/all.min.css";
import "react-toastify/dist/ReactToastify.css";

import PrivateRoute from "./routes/Private-Routes";
import PublicRoute from "./routes/Public-Routes";
import { addNotificationData, addUserPermissionData } from "./redux/userSlice";
import { addCmsData } from "./redux/cmsSlice";
import { addScreenSize } from "./redux/screenSize.js";

export const base_url = process.env.REACT_APP_BASE_URL;
export const api_type = process.env.REACT_APP_API_TYPE;

// Public entry pages are also lazy-loaded so route chunks are only requested when needed.
const LoginMainPage = lazy(() => import("./pages/LoginNew/index.jsx"));

// Primary private route pages are lazy-loaded to keep the initial bundle smaller and improve
// the app's first-load performance.
const RegistrationManagementNewPage = lazy(
  () => import("./pages/RegistrationManagementNew/index.jsx")
);
const ClientManagementNewPage = lazy(
  () => import("./pages/ClientManagementNew/index.jsx")
);
const BondManagementNewPage = lazy(
  () => import("./pages/BondManagementNew/index.jsx")
);
const ContentManagementNewPage = lazy(
  () => import("./pages/ContentManagementNew/index.jsx")
);
const ControlCenterNewPage = lazy(
  () => import("./pages/ControlCenterNew/index.jsx")
);
const UserManagementNewPage = lazy(
  () => import("./pages/UserManagementNew/index.jsx")
);
const DashboardNewPage = lazy(() => import("./pages/DashboardNew/index.jsx"));
const CmsList = lazy(() => import("./pages/CMS/CmsList"));
const ReportMain = lazy(() => import("./pages/Report/ReportMain"));
const InvestorReportEdit = lazy(
  () => import("./pages/Report/InvestorReportEdit")
);
const UserManagementMain = lazy(
  () => import("./pages/UserManagement/UserManagementMain")
);
const UserCreation = lazy(() => import("./pages/UserManagement/UserCreation"));
const AddUserManagement = lazy(
  () => import("./pages/UserManagement/AddUserManagement")
);
const EditUserManagement = lazy(
  () => import("./pages/UserManagement/edituserManagement")
);
const InvestorManagementMainPage = lazy(
  () => import("./pages/InvestorManagement")
);
const InvestorDetails = lazy(
  () => import("./pages/InvestorManagement/InvestorDetails")
);
const InvestorManagementPartner = lazy(
  () => import("./pages/InvestorManagement/InvestorManagmentParnter")
);
const InvestorManagementParnterDetails = lazy(
  () => import("./pages/InvestorManagement/InvestorManagmentPartnerDetails")
);
const BondManagementMainPage = lazy(
  () => import("./pages/BondManagement/index.jsx")
);
const HomePagemanage = lazy(() => import("./pages/CMS/HomePage"));
const ContactUs = lazy(() => import("./pages/CMS/contactUs"));
const FaqManage = lazy(() => import("./pages/CMS/Faq"));
const AddFaq = lazy(() => import("./pages/CMS/Faq/addFaq"));
const EditFaq = lazy(() => import("./pages/CMS/Faq/editFaq"));
const BlogManage = lazy(() => import("./pages/CMS/Blog"));
const AboutUsManage = lazy(() => import("./pages/CMS/AboutUs"));
const Announcement = lazy(() => import("./pages/CMS/Announcement"));
const RegistrationPartnerDetails = lazy(
  () => import("./pages/RegistartionManagement/RegistrationPartnerDetails")
);
const RelationshipManagerMain = lazy(
  () => import("./pages/RelationshipManager/RelationshipManagerMain")
);
const SellRequestMain = lazy(() => import("./pages/SellRequest/SellRequest"));
const InviteWealthPartner = lazy(() => import("./pages/InviteWP"));
const ManageClientMainPage = lazy(() => import("./pages/ManageClient"));
const ManageResourceMainPage = lazy(() => import("./pages/ManageResounces"));
const ManageResouncesNewMainPage = lazy(
  () => import("./pages/ManageResounces/mainPage.jsx")
);
const LearningVideoPlaylistPage = lazy(
  () => import("./pages/ManageResounces/Training/LearningVideoAsset")
);
const LearningVideoViewPlaylist = lazy(
  () =>
    import(
      "./pages/ManageResounces/Training/LearningVideoAsset/LearningVideoViewPlaylist"
    )
);
const HandbookAssetsListPage = lazy(
  () =>
    import("./pages/ManageResounces/Training/Handbook/HandbookAssetList")
);

// Legacy standalone routes are preserved as-is, but still lazy-loaded for better chunking.
const FounderNotePage = lazy(
  () => import("./pages/CMS/FIDCms/FounderNoteEditor")
);
const FaqCategoriesPage = lazy(
  () => import("./pages/CMS/FIDCms/FaqCategories")
);
const FaqsPage = lazy(() => import("./pages/CMS/FIDCms/Faqs"));
const AboutSectionsPage = lazy(
  () => import("./pages/CMS/FIDCms/AboutSections")
);
const BondDirectory = lazy(
  () => import("./pages/BondDirectory/BondDirectory.jsx")
);
const FileViewerPage = lazy(() => import("./pages/FileViewer/index.jsx"));

// Lightweight route fallback shown while lazy route chunks are loading.
function RouteLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center text-sm text-[#6B7A90]">
      Loading...
    </div>
  );
}

function App() {
  const dispatch = useDispatch();
  const { authToken, isAuthenticated } = useSelector((state) => state.login);

  // Normalize auth into one boolean so route guards behave consistently even when Redux
  // restores persisted string values from localStorage.
  const isUserAuthenticated = Boolean(
    authToken || isAuthenticated === true || isAuthenticated === "true"
  );

  // Fetch permission and notification data used across protected screens.
  const getPermission = useCallback(async () => {
    const endpoint = `${base_url}/${api_type}/permissionsData`;
    const token = authToken || localStorage.getItem("token");

    try {
      const permissionResponse = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      dispatch(
        addUserPermissionData(permissionResponse?.data?.data?.permissions)
      );
      dispatch(
        addNotificationData(permissionResponse?.data?.data?.notifications)
      );
    } catch (error) {
      console.log("permissionResponse error", error);
    }
  }, [authToken, dispatch]);

  // Fetch CMS bootstrap content once an authenticated session is available.
  const getCmsData = useCallback(async () => {
    const endpoint = `${base_url}/${api_type}/cms/homeContent/get`;
    const token = authToken || localStorage.getItem("token");

    try {
      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      dispatch(addCmsData(response?.data?.data));
    } catch (err) {
      console.error("Failed to fetch bond details:", err);
    }
  }, [authToken, dispatch]);

  // Keep the shared screen-size slice in sync with the viewport while throttling resize work
  // through requestAnimationFrame to avoid excessive dispatching.
  useEffect(() => {
    let frameId = 0;

    const updateScreenSize = () => {
      dispatch(addScreenSize(window.innerWidth));
    };

    const handleResize = () => {
      cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(updateScreenSize);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener("resize", handleResize);
    };
  }, [dispatch]);

  // Bootstrap permission and CMS data in parallel after authentication so the dashboard
  // and navigation can render with the required shared data.
  useEffect(() => {
    if (!isUserAuthenticated) return;

    const bootstrapAuthenticatedApp = async () => {
      await Promise.all([getPermission(), getCmsData()]);
    };

    bootstrapAuthenticatedApp();
  }, [getCmsData, getPermission, isUserAuthenticated]);

  // Grouping routes into config arrays keeps the routing tree easier to maintain and makes
  // it straightforward to reuse the same auth wrappers.
  const publicRoutes = [
    { path: "/", element: <LoginMainPage /> },
    { path: "/login", element: <LoginMainPage /> },
    { path: "/login/:url", element: <LoginMainPage /> },
    ];

  const privateRoutes = [
    { path: "/registration/:url", element: <RegistrationManagementNewPage /> },
    {
      path: "/registration/investor/detail/:id",
      element: <RegistrationManagementNewPage />,
    },
    {
      path: "/registration/investor/deletion-log",
      element: <RegistrationManagementNewPage />,
    },
    {
      path: "/registration/distributor/detail/:id",
      element: <RegistrationManagementNewPage />,
    },
    {
      path: "/registration/distributor/deletion-log",
      element: <RegistrationManagementNewPage />,
    },
    { path: "/client-management/:url", element: <ClientManagementNewPage /> },
    { path: "/bond-management/:url", element: <BondManagementNewPage /> },
    {
      path: "/content-management/:url",
      element: <ContentManagementNewPage />,
    },
    {
      path: "/content-management/manage-resource/:url",
      element: <ContentManagementNewPage />,
    },
    { path: "/control-center/:url", element: <ControlCenterNewPage /> },
    { path: "/user-management", element: <UserManagementNewPage /> },
    { path: "/cms/:type", element: <CmsList /> },
    {
      path: "/registration/wealth-partner/details",
      element: <RegistrationPartnerDetails />,
    },
    { path: "/dashboard", element: <DashboardNewPage /> },
    {
      path: "/bond-management/bond-catalogue",
      element: <BondManagementMainPage />,
    },
    { path: "/bond/edit", element: <BondManagementMainPage /> },
    { path: "/bond/add", element: <BondManagementMainPage /> },
    { path: "/report", element: <ReportMain /> },
    { path: "/report/edit", element: <InvestorReportEdit /> },
    { path: "/user", element: <UserManagementMain /> },
    { path: "/user/creation", element: <UserCreation /> },
    { path: "/user/new-user", element: <AddUserManagement /> },
    { path: "/user/edit-user/:userId", element: <EditUserManagement /> },
    {
      path: "/investment-management/investor",
      element: <InvestorManagementMainPage />,
    },
    {
      path: "/investment-management/investor/details",
      element: <InvestorDetails />,
    },
    {
      path: "/investment-management/wealth-partner",
      element: <InvestorManagementPartner />,
    },
    {
      path: "/investment-management/wealth-partner/details",
      element: <InvestorManagementParnterDetails />,
    },
    { path: "/cms/homepage", element: <HomePagemanage /> },
    { path: "/cms/contactus", element: <ContactUs /> },
    { path: "/cms/faq", element: <FaqManage /> },
    { path: "/cms/add/faq", element: <AddFaq /> },
    { path: "/cms/edit/faq", element: <EditFaq /> },
    { path: "/cms/blog", element: <BlogManage /> },
    { path: "/cms/aboutus", element: <AboutUsManage /> },
    { path: "/cms/announcement", element: <Announcement /> },
    { path: "/relationship-manager", element: <RelationshipManagerMain /> },
    { path: "/sell-request", element: <SellRequestMain /> },
    { path: "/invite-partner", element: <InviteWealthPartner /> },
    { path: "/manage-client/:name", element: <ManageClientMainPage /> },
    { path: "/manage-resource", element: <ManageResourceMainPage /> },
    { path: "/manage-resource/:id", element: <ManageResouncesNewMainPage /> },
    {
      path: "/manage-resource/training/learning/:id",
      element: <LearningVideoPlaylistPage />,
    },
    {
      path: "/manage-resource/training/learning/playlist/:id",
      element: <LearningVideoViewPlaylist />,
    },
    {
      path: "/manage-resource/training/handbook/:id",
      element: <HandbookAssetsListPage />,
    },
  ];

  // These routes previously bypassed the auth wrapper, so they are preserved that way here
  // to avoid changing existing route behavior unexpectedly.
  const standaloneRoutes = [
    { path: "/cms/founder-note", element: <FounderNotePage /> },
    { path: "/cms/faq-categories", element: <FaqCategoriesPage /> },
    { path: "/cms/faqs", element: <FaqsPage /> },
    { path: "/cms/about", element: <AboutSectionsPage /> },
    { path: "/bond-directory", element: <BondDirectory /> },
    { path: "/file-viewer", element: <FileViewerPage /> },
  ];

  // Wrapper helpers keep the route declarations compact and consistent.
  const renderPublicRoute = (element) => (
    <PublicRoute isAuthenticated={isUserAuthenticated}>{element}</PublicRoute>
  );

  const renderPrivateRoute = (element) => (
    <PrivateRoute isAuthenticated={isUserAuthenticated}>{element}</PrivateRoute>
  );

  return (
    <>
      <ToastContainer />
      <Router>
        <Suspense fallback={<RouteLoader />}>
          <Routes>
            {publicRoutes.map(({ path, element }) => (
              <Route
                key={path}
                path={path}
                element={renderPublicRoute(element)}
              />
            ))}

            {privateRoutes.map(({ path, element }) => (
              <Route
                key={path}
                path={path}
                element={renderPrivateRoute(element)}
              />
            ))}

            {standaloneRoutes.map(({ path, element }) => (
              <Route key={path} path={path} element={element} />
            ))}
          </Routes>
        </Suspense>
      </Router>
    </>
  );
}

export default App;
